import { Controller } from "@hotwired/stimulus"
import { attachHereAutocomplete } from "../helpers/here_places"

// Einsatzort of one timesheet entry block on a Sammelkostenstelle (TimesheetWorkLocation).
//
// Mounted on the entry block, so every multi-entry clone runs its own instance. The block
// is shown only while the block's site select holds a Sammelkostenstelle; otherwise its
// inputs are disabled so a hidden block never submits a stale location (the server clears
// the location on an ordinary site anyway).
//
// Three ways in, each leaving a consistent set of hidden fields:
//   - typing an address   -> source "address", no coordinates (the server geocodes)
//   - picking a suggestion -> source "address" with the suggestion's coordinates
//   - "Standort ermitteln" -> source "gps" with the device coordinates, address looked up
//   - a site location chip -> source "site_location" + its id (the server snapshots it)
//
// Every settled change dispatches "work-location:changed" (bubbling): on a Sammelkostenstelle
// the Einsatzort decides the Vergütete-Fahrzeiten drive, so site_driving_time_controller
// re-fetches this block's figure. Keystrokes do not count — a typed address settles on
// "change" (blur), where the server geocodes it.
export default class extends Controller {
  static targets = [
    "siteSelect",
    "block",
    "address",
    "latitude",
    "longitude",
    "source",
    "siteLocation",
    "status",
    "choices",
    "choiceList",
    "locateButton",
  ]

  // siteIds: bookable Sammelkostenstellen. locations: { siteId: [{ id, label, address }] }.
  static values = { siteIds: Array, locations: Object, lookupUrl: String }

  connect() {
    if (this.hasAddressTarget) {
      this.teardownAutocomplete = attachHereAutocomplete(this.addressTarget, {
        onSelect: (fields) => this.suggestionChosen(fields),
      })
    }
    this.sync({ clearOnHide: true })
  }

  disconnect() {
    if (this.teardownAutocomplete) this.teardownAutocomplete()
  }

  siteChanged() {
    // A location belongs to one site: a new site starts from an empty field.
    this.clearLocation()
    this.sync({ clearOnHide: true })
  }

  addressTyped() {
    // Hand-typed text no longer matches coordinates or a picked site location.
    this.setHidden({ latitude: "", longitude: "", source: "address", siteLocation: "" })
    this.markChoice(null)
    this.showStatus(null)
  }

  addressCommitted() {
    this.announceChange()
  }

  preventSubmit(event) {
    if (event.key === "Enter") event.preventDefault()
  }

  suggestionChosen(fields) {
    this.addressTarget.value = fields.label || fields.street || ""
    const position = fields.position || {}
    this.setHidden({
      latitude: position.lat ?? "",
      longitude: position.lng ?? "",
      source: "address",
      siteLocation: "",
    })
    this.markChoice(null)
    this.announceChange()
  }

  choose(event) {
    const { locationId, locationAddress } = event.currentTarget.dataset
    this.addressTarget.value = locationAddress || ""
    // Coordinates come from the server-side snapshot of the site location.
    this.setHidden({
      latitude: "",
      longitude: "",
      source: "site_location",
      siteLocation: locationId,
    })
    this.markChoice(locationId)
    this.showStatus(null)
    this.announceChange()
  }

  locate() {
    if (!("geolocation" in navigator)) {
      this.showStatus("unsupported")
      return
    }

    this.showStatus("locating")
    navigator.geolocation.getCurrentPosition(
      (position) => this.located(position.coords),
      () => this.showStatus("failed"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    )
  }

  async located(coords) {
    const latitude = coords.latitude.toFixed(7)
    const longitude = coords.longitude.toFixed(7)
    this.setHidden({ latitude, longitude, source: "gps", siteLocation: "" })
    this.addressTarget.value = ""
    this.markChoice(null)
    this.showStatus("located")
    this.announceChange()

    // The saved entry resolves the address server-side regardless; this only shows it now.
    try {
      const url = new URL(this.lookupUrlValue, window.location.origin)
      url.searchParams.set("latitude", latitude)
      url.searchParams.set("longitude", longitude)
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) return
      const { address } = await response.json()
      // Only if the user has not typed or picked something else in the meantime.
      if (address && this.sourceTarget.value === "gps" && this.addressTarget.value === "") {
        this.addressTarget.value = address
        this.showStatus(null)
      }
    } catch {
      // Keep the coordinates; the server fills the address after saving.
    }
  }

  // --- helpers --------------------------------------------------------------

  sync({ clearOnHide }) {
    const siteId = this.selectedSiteId
    const active = siteId !== null && this.siteIdsValue.includes(siteId)

    if (this.hasBlockTarget) this.blockTarget.classList.toggle("hidden", !active)
    this.inputs.forEach((input) => {
      input.disabled = !active
    })
    if (!active && clearOnHide) this.clearLocation()
    this.renderChoices(active ? this.locationsValue[String(siteId)] || [] : [])
  }

  renderChoices(locations) {
    if (!this.hasChoiceListTarget) return

    this.choiceListTarget.replaceChildren(
      ...locations.map((location) => {
        const button = document.createElement("button")
        button.type = "button"
        button.className =
          "rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-teal-400 hover:text-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-500"
        button.textContent = location.label
        button.title = location.address || ""
        button.dataset.locationId = String(location.id)
        button.dataset.locationAddress = location.address || ""
        button.dataset.action = "click->work-location#choose"
        return button
      }),
    )
    if (this.hasChoicesTarget) this.choicesTarget.classList.toggle("hidden", locations.length === 0)
    this.markChoice(this.hasSiteLocationTarget ? this.siteLocationTarget.value : null)
  }

  markChoice(locationId) {
    if (!this.hasChoiceListTarget) return

    this.choiceListTarget.querySelectorAll("button").forEach((button) => {
      const chosen = locationId && button.dataset.locationId === String(locationId)
      button.classList.toggle("border-teal-500", Boolean(chosen))
      button.classList.toggle("bg-teal-100", Boolean(chosen))
      button.setAttribute("aria-pressed", chosen ? "true" : "false")
    })
  }

  clearLocation() {
    if (this.hasAddressTarget) this.addressTarget.value = ""
    this.setHidden({ latitude: "", longitude: "", source: "", siteLocation: "" })
    this.showStatus(null)
    this.announceChange()
  }

  announceChange() {
    this.dispatch("changed")
  }

  setHidden(values) {
    Object.entries(values).forEach(([name, value]) => {
      const hasTarget = this[`has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`]
      if (hasTarget) this[`${name}Target`].value = value
    })
  }

  showStatus(key) {
    if (!this.hasStatusTarget) return

    const text = key && this.element.querySelector(`[data-work-location-text="${key}"]`)
    this.statusTarget.textContent = text ? text.textContent : ""
    this.statusTarget.classList.toggle("hidden", !text)
  }

  get inputs() {
    return [
      this.hasAddressTarget && this.addressTarget,
      this.hasLatitudeTarget && this.latitudeTarget,
      this.hasLongitudeTarget && this.longitudeTarget,
      this.hasSourceTarget && this.sourceTarget,
      this.hasSiteLocationTarget && this.siteLocationTarget,
    ].filter(Boolean)
  }

  get selectedSiteId() {
    if (!this.hasSiteSelectTarget) return null

    const value = parseInt(this.siteSelectTarget.value, 10)
    return Number.isNaN(value) ? null : value
  }
}
